/** 대사할 파일 두 칸 — `[이전, 최신]`. */
export type FileSlots = [File | null, File | null];

export const isDsd = (file: File) => file.name.toLowerCase().endsWith(".dsd");

export const PER_FORMAT_ERROR = "파일은 형식별로 1개씩 업로드해 주세요.";
export const DUPLICATE_ERROR =
  "이미 업로드한 파일입니다. 다른 파일을 업로드해 주세요.";

/** `fillFileSlots` 가 받을 수 있는 칸 수 — 다 찼으면 "교체" 한 칸만 받는다. */
const slotRoom = (slots: FileSlots) =>
  slots.filter((file) => !file).length || 1;

/**
 * 빈 칸을 이전 → 최신 순으로 채운다. 둘 다 찼으면 최신 칸을 교체한다.
 *
 * 빈 칸보다 많이 고른 경우는 호출부가 `overflowError` 로 먼저 막는다 — 여기서는 남는 만큼만
 * 버린다. `FileDropArea` 의 "앞서 고른 파일을 조용히 덮지 않는다" 계약이 한 드롭 안에서도 서야 한다.
 */
export const fillFileSlots = (slots: FileSlots, picked: File[]): FileSlots => {
  const room = slotRoom(slots);
  return picked
    .slice(0, room)
    .reduce<FileSlots>(([p, l], file) => (p ? [p, file] : [file, l]), slots);
};

export const slotRoomError = (room: number) =>
  `남은 칸이 ${room}개입니다. 파일을 ${room}개까지 선택해 주세요.`;

/**
 * 빈 칸보다 많이 고르면 알릴 문구. 문제 없으면 `null`.
 * `fillFileSlots` 는 넘치는 만큼을 조용히 버리므로, 호출부는 이 함수로 먼저 막아
 * 아무 칸도 바꾸지 않고 업로드 영역 아래 인라인으로 알린다.
 * (상한 `maxFiles` 초과는 그 전에 `FileDropArea` 가 통째로 막는다.)
 */
export const overflowError = (
  slots: FileSlots,
  picked: File[],
): string | null => {
  const room = slotRoom(slots);
  return picked.length > room ? slotRoomError(room) : null;
};

/**
 * 확장자로 DSD/IXD 칸을 정한다. 같은 형식을 둘 이상 고르면 어느 쪽을 쓸지 정할 수 없으므로
 * 아무 칸도 채우지 않고 `error` 만 돌려준다 — 호출부가 업로드 영역 아래 인라인으로 알린다.
 * 고르지 않은 쪽은 `null` — 호출부가 기존 값을 둔다.
 */
export const splitDsdIxd = (picked: File[]) => {
  const dsd = picked.filter(isDsd);
  const ixd = picked.filter((file) => !isDsd(file));
  if (dsd.length > 1 || ixd.length > 1) {
    return { dsd: null, ixd: null, error: PER_FORMAT_ERROR };
  }
  return { dsd: dsd[0] ?? null, ixd: ixd[0] ?? null, error: null };
};

/**
 * 같은 파일인지. 파일명만으로는 이전/최신 보고서가 동명일 때 오탐이라 크기·수정시각까지 본다.
 * ponytail: 내용 해시까지는 안 본다 — 같은 파일을 다시 고른 경우를 막는 것이 목적이다.
 */
const isSameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/**
 * 같은 파일을 또 고르면 알릴 문구. 문제 없으면 `null`.
 * 기존 칸뿐 아니라 **한 번에 고른 파일끼리도** 본다 — 검색 결과 뷰처럼 여러 폴더의
 * 파일을 한 드롭에 담을 수 있는 경로로는 동명·동크기 사본 2개가 그대로 들어온다.
 */
export const duplicateError = (
  slots: (File | null)[],
  picked: File[],
): string | null =>
  picked.some(
    (file, i) =>
      slots.some((slot) => slot && isSameFile(slot, file)) ||
      picked.slice(0, i).some((prev) => isSameFile(prev, file)),
  )
    ? DUPLICATE_ERROR
    : null;
